import { Container } from '@socialchorus/shared-ui-components';
import { FC, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import { BaseTab } from '../BaseTab';
import { useTopicShortcutsFetcher } from '../../../../../common/use-topic-shortcuts-fetcher';
import { Spinner } from '../../../../ui';
import EmptyPlaceholder from '../../common/EmptyPlaceholder';
import { TopicPageShortcut } from './shortcut';
import styles from './styles.module.scss';
import { uiOperations } from '../../../../../models/ui';
import {
  ShortcutModalProps,
  ID as ShortcutsModalId,
} from '../../../../ui/shortcut/modal/shortcut-modal';

interface Props {
  topicId: string;
  activeTabId: number;
  shortcutId?: string;
}

const ShortcutsTab: FC<Props> = ({ topicId, activeTabId, shortcutId }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const { shortcuts, isError, isFetching } = useTopicShortcutsFetcher(topicId);
  const hasOpenedModal = useRef(false);

  // Open modal when shortcutId is in URL and shortcuts are loaded
  useEffect(() => {
    if (!shortcutId || isFetching || !shortcuts || hasOpenedModal.current) {
      return;
    }

    const shortcut = shortcuts.find((s) => String(s.id) === shortcutId);

    if (shortcut) {
      hasOpenedModal.current = true;

      // Custom close handler that navigates to shortcuts tab
      // The overlay object is passed by OverlayController with { id, key, props }
      const handleClose = (overlay: { id: string; key: string }) => {
        dispatch(uiOperations.removeOverlay(overlay));
        history.replace(`/channels/${topicId}/shortcuts`);
      };

      dispatch(
        uiOperations.displayOverlay(ShortcutsModalId, {
          title: shortcut.name,
          description: shortcut.description,
          items: shortcut.links.map(({ name, url }) => ({ label: name, url })),
          close: handleClose,
        } as ShortcutModalProps)
      );
    }
  }, [shortcutId, shortcuts, isFetching, dispatch, history, topicId]);

  // Reset the flag when shortcutId changes
  useEffect(() => {
    hasOpenedModal.current = false;
  }, [shortcutId]);

  return (
    <BaseTab activeTabId={activeTabId} topicId={topicId}>
      {isError && !isFetching && (
        <Container className={styles.Container} shadow="extra-light" fullWidth>
          {t('screens.topics.shortcuts.error')}
        </Container>
      )}
      {isFetching && <Spinner />}
      {!isFetching &&
        (shortcuts?.length ? (
          <Container className={styles.Container} shadow="extra-light">
            <span className="text-body text-black-60">
              {t('screens.topics.shortcuts.header', {
                count: shortcuts.length,
              })}
            </span>
            <div className={styles.ShortcutsGrid}>
              {shortcuts.map((shortcut) => (
                <TopicPageShortcut shortcut={shortcut} key={shortcut.id} />
              ))}
            </div>
          </Container>
        ) : (
          <EmptyPlaceholder
            title={t('screens.topics.shortcuts.empty.title')}
            description={t('screens.topics.shortcuts.empty.description')}
          />
        ))}
    </BaseTab>
  );
};

export default ShortcutsTab;
